import React, { createContext, useContext, useState, useEffect } from 'react';

const OnboardingContext = createContext();

export const useOnboarding = () => {
  const context = useContext(OnboardingContext);
  if (!context) {
    throw new Error('useOnboarding must be used within OnboardingProvider');
  }
  return context;
};

export const OnboardingProvider = ({ children }) => {
  const [isOnboardingActive, setIsOnboardingActive] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [hasCompletedOnboarding, setHasCompletedOnboarding] = useState(false);

  // Onboarding steps definition
  const steps = [
    {
      id: 'welcome',
      title: 'Bem-vindo ao Alvo! 🎯',
      description: 'Seu copiloto financeiro está pronto para te ajudar a ter controle total do seu negócio.',
      target: null,
      position: 'center',
      action: null
    },
    {
      id: 'hub',
      title: 'Navegação por Áreas 🧭',
      description: 'Ao entrar, você cai na tela inicial com as grandes áreas: Visão Geral, Planejamento, Financeiro, Performance, Diretor Financeiro e Configurações.\n• Clique numa área para entrar — a lateral passa a mostrar **só** as telas daquela área.\n• Use "← Todas as áreas" (ou o logo) para voltar ao início.\n• O caminho no topo (ex.: Financeiro › Fluxo de Caixa) mostra onde você está.',
      target: null,
      position: 'center',
      route: '/areas'
    },
    {
      id: 'dashboard',
      title: 'Visão Geral — Seu Painel',
      description: 'Dentro de "Visão Geral" fica o Dashboard, que abre no "Resumo do Mês". No topo dá pra trocar para os painéis Executivo, de Despesas ou de Faturamento — e escolher o período (um mês específico ou um intervalo personalizado).',
      target: '[data-testid="dashboard-summary"]',
      position: 'bottom',
      route: '/dashboard'
    },
    {
      id: 'faturamento',
      title: 'Cadastre seu Faturamento',
      description: 'Registre suas vendas em Gestão de Receita, escolhendo a fonte e o perfil de recebimento (à vista, parcelado, antecipado). A venda aparece automaticamente no DRE por competência.',
      target: '[data-testid="add-venda-btn"]',
      position: 'bottom',
      route: '/gestao-receita',
      highlight: true
    },
    {
      id: 'parcelamento',
      title: 'Simulador de Parcelamento e Antecipação',
      description: 'Vendeu parcelado? Diga em quantas vezes e se quer antecipar — o sistema calcula em que mês cada real vai cair.\n• R$ 45k à vista → cai 45k no próprio mês.\n• R$ 20k em 2x sem antecipar → 10k no mês seguinte + 10k dois meses depois.\n• Isso alimenta a coluna **Estimativa** do Fluxo.',
      target: '[data-testid="venda-perfil-preview-text"]',
      position: 'right',
      route: '/gestao-receita',
      highlight: true
    },
    {
      id: 'receita',
      title: 'Configure suas Fontes de Receita',
      description: 'Defina de onde vem seu dinheiro: mensalidades, vendas, serviços. Configure o perfil de recebimento padrão de cada fonte na aba "Fontes de Receita".',
      target: '[data-testid="tab-fontes"]',
      position: 'bottom',
      route: '/gestao-receita'
    },
    {
      id: 'despesas',
      title: 'DRE - Despesas por Competência (manual)',
      description: 'Cadastre aqui as despesas que pertencem ao mês (competência). Aqui **não** tem OFX/importação.\n• **DRE** = competência (o que é do mês).\n• **Fluxo** = caixa real (o que saiu do banco via OFX).\n• Os dois mundos são separados — nada duplica.',
      target: '[data-testid="add-expense-btn"]',
      position: 'bottom',
      route: '/dre',
      highlight: true
    },
    {
      id: 'caixa',
      title: 'Caixa - Movimentação Real',
      description: 'Importe seu extrato OFX ou lance manualmente — o Caixa é o dinheiro que entrou e saiu de verdade.\n• **Entradas**: "Recebimento de cliente" abate a carteira a receber; aportes e empréstimos ficam separados.\n• **Saídas**: escolha a categoria (de Configurações). O tipo dela manda — Operacional cria a despesa no DRE e vai p/ "Saídas OP"; Pontual vai p/ "Desp. Pontual"; Investimento vai p/ "Invest.".\n• **Transferência** entre suas contas: marque como "Transferência" — mexe no saldo, mas não é receita nem despesa.\n• **Ferramentas**: aba "Sem Conta" vincula lançamentos antigos, "Recalcular saldo" conserta o saldo, e Regras categorizam o OFX pela descrição.',
      target: null,
      position: 'center',
      route: '/caixa'
    },
    {
      id: 'categorias',
      title: 'Configure Categorias (Operacional / Pontual / Investimento)',
      description: 'Em Configurações › Categorias, cada despesa ganha um tipo — e é ele que o sistema lê ao importar o OFX.\n• **Operacional**: entra no DRE do mês e na Projeção.\n• **Pontual**: gasto único — só aparece no Fluxo (não projeta nem entra no DRE).\n• **Investimento**: maquinário, obras — só no Fluxo.\n• Mudou o tipo aqui? O sistema recalcula sozinho o DRE e o Fluxo das movimentações já importadas dessa categoria.',
      target: null,
      position: 'center',
      route: '/settings'
    },
    {
      id: 'compromissos',
      title: 'Financiamentos e Empréstimos',
      description: 'Cadastre financiamentos, empréstimos e investimentos com o cronograma de parcelas.\n• O cronograma alimenta a **Projeção** (a previsão do futuro).\n• No Fluxo, a parcela só aparece quando você importa e classifica o débito real no OFX como "Parcela de Financiamento".\n• Assim o Fluxo mostra só o que realmente saiu — nada duplica.',
      target: null,
      position: 'center',
      route: '/compromissos'
    },
    {
      id: 'fluxo',
      title: 'Fluxo de Caixa - O que aconteceu',
      description: 'O Fluxo mostra o que realmente aconteceu no caixa, mês a mês. Colunas principais:\n• **Estimativa**: o que o cadastro (Gestão de Receita) diz que devia entrar no mês — é ela que entra nos cálculos.\n• **A Receber**: o gap do mês — o que foi faturado no mês e ainda vai cair (vendas a prazo). É só informativo.\n• **Recebido Real**: o que caiu de fato no OFX.\n• **Diferença Bancária**: Recebido Real − Estimativa (positivou/negativou) — reflete taxas, antecipação ou atraso.\n• **Saídas** em 3 colunas pelo tipo da categoria: "Saídas OP", "Desp. Pontual" e "Invest.".\n• **Lucro Líquido** = Estimativa − Saídas OP − Pontual − Investimento; **Margem** = Lucro ÷ Estimativa.\n• Dica: clique em qualquer número para ver de onde ele veio.',
      target: null,
      position: 'center',
      route: '/fluxo-caixa'
    },
    {
      id: 'projecao',
      title: 'Projeção de Caixa - O que vem pela frente',
      description: 'Projeta os próximos meses a partir do saldo atual + médias do Fluxo, já incluindo as parcelas futuras dos seus compromissos.\n• 3 cenários: pessimista, realista e otimista.\n• Gráfico de evolução do saldo.\n• Ajuste os fatores em Configurações › Cenários.',
      target: null,
      position: 'center',
      route: '/projecao'
    },
    {
      id: 'saude',
      title: 'Saúde Financeira',
      description: 'O raio-x do negócio, com números **globais** (todo o histórico, não só o mês).\n• Caixa real e evolução do saldo mês a mês.\n• **Carteira a receber**: tudo que você faturou **menos** tudo que já recebeu de cliente (OFX). O que sobra é o que ainda tem a receber — parcelas futuras (ex.: 12x) e qualquer valor que faturou e ainda não caiu. O à vista sai quando o OFX mostra que caiu; antecipação (recebeu mais) abate; aporte/transferência não entram.\n• Indicadores-chave globais: Margem Líquida, ROI (Lucro ÷ Investimento), Liquidez e Endividamento.\n• Score de saúde e estrutura do caixa.\n• Dica: clique em qualquer número para ver a explicação e de onde ele veio.',
      target: null,
      position: 'center',
      route: '/saude-financeira'
    },
    {
      id: 'capacidade',
      title: 'Capacidade de Investimento 💰',
      description: 'Em Saúde (card "Cap. Investir") ou na Projeção, clique no valor para ver a **conta aberta**: geração de caixa − compromissos − pró-labore − pontuais, com fator de segurança e reserva mínima.\n• Ajuste os parâmetros (tudo editável, com "voltar ao padrão").\n• Simule uma parcela e veja na hora se **cabe** no seu mês — sem colocar o caixa em risco.',
      target: null,
      position: 'center',
      route: '/saude-financeira'
    },
    {
      id: 'metas_orcamento',
      title: 'Metas & Orçamento 🎯',
      description: 'Dois conceitos, cada um em sua aba:\n• **Metas**: objetivos de resultado que você quer atingir ou superar (Faturamento, Lucro, Margem, ROI).\n• **Orçamento**: o teto de gasto que você não quer estourar (Despesas, Folha, custos).\n• Cada um pode ser **Mensal** ou **Anual** (no anual o sistema soma o ano todo), e você cria vários de uma vez.\n• Verde = dentro/atingido; vermelho = estourou. Acompanhe o resultado na aba **Performance**.',
      target: null,
      position: 'center',
      route: '/goals'
    },
    {
      id: 'performance',
      title: 'Performance — Metas x Realidade 📊',
      description: 'Mostra o quanto você está batendo as **Metas** e ficando dentro dos **Orçamentos**.\n• Veja de forma **Mensal** ou **Anual**.\n• Medidores visuais: verde = no alvo, vermelho = fora.\n• A "Evolução Anual" mostra a performance mês a mês.',
      target: null,
      position: 'center',
      route: '/performance'
    },
    {
      id: 'diretor',
      title: 'Diretor Financeiro IA 🤖',
      description: 'Feche o mês para receber uma análise estratégica completa da IA sobre seu negócio.',
      target: '[data-testid="close-month-button"]',
      position: 'bottom',
      route: '/diretor-financeiro',
      highlight: true
    },
    {
      id: 'complete',
      title: 'Tudo Pronto! 🚀',
      description: 'Agora é só começar a usar. Dica: comece cadastrando o faturamento e despesas dos últimos 3 meses para ter análises mais precisas.',
      target: null,
      position: 'center',
      action: 'complete'
    }
  ];

  // Load state from localStorage
  useEffect(() => {
    const completed = localStorage.getItem('alvo_onboarding_completed');
    const savedStep = localStorage.getItem('alvo_onboarding_step');
    
    if (completed === 'true') {
      setHasCompletedOnboarding(true);
    } else if (savedStep) {
      setCurrentStep(parseInt(savedStep, 10));
    }
  }, []);

  // Save step to localStorage
  useEffect(() => {
    if (isOnboardingActive) {
      localStorage.setItem('alvo_onboarding_step', currentStep.toString());
    }
  }, [currentStep, isOnboardingActive]);

  const startOnboarding = () => {
    setCurrentStep(0);
    setIsOnboardingActive(true);
    setHasCompletedOnboarding(false);
    localStorage.removeItem('alvo_onboarding_completed');
  };

  const nextStep = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      completeOnboarding();
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const skipOnboarding = () => {
    completeOnboarding();
  };

  const completeOnboarding = () => {
    setIsOnboardingActive(false);
    setHasCompletedOnboarding(true);
    localStorage.setItem('alvo_onboarding_completed', 'true');
    localStorage.removeItem('alvo_onboarding_step');
  };

  const goToStep = (stepIndex) => {
    if (stepIndex >= 0 && stepIndex < steps.length) {
      setCurrentStep(stepIndex);
    }
  };

  const value = {
    isOnboardingActive,
    currentStep,
    steps,
    currentStepData: steps[currentStep],
    hasCompletedOnboarding,
    totalSteps: steps.length,
    progress: ((currentStep + 1) / steps.length) * 100,
    startOnboarding,
    nextStep,
    prevStep,
    skipOnboarding,
    completeOnboarding,
    goToStep
  };

  return (
    <OnboardingContext.Provider value={value}>
      {children}
    </OnboardingContext.Provider>
  );
};

export default OnboardingContext;
